import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Droplets, Target,
  UtensilsCrossed, Search, Settings2, ChevronDown, ChevronUp,
  TrendingUp, Flame, Droplet, Share2
} from "lucide-react";
import { format, addDays, subDays, parseISO } from "date-fns";
import ShareDialog from "@/components/share-dialog";

const MEAL_TYPES = [
  { id: "breakfast", label: "Breakfast", icon: "🌅" },
  { id: "lunch", label: "Lunch", icon: "☀️" },
  { id: "dinner", label: "Dinner", icon: "🌙" },
  { id: "snacks", label: "Snacks", icon: "🍎" },
];

const WATER_QUICK = [150, 250, 350, 500];

interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  saturatedFat: number;
  transFat: number;
  sodium: number;
  servingSize: number;
  servingUnit: string;
  isCustom?: boolean;
}

interface DiaryEntry {
  id: string;
  date: string;
  mealType: string;
  foodName: string;
  quantity: number;
  servingUnit: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  saturatedFat: number;
  transFat: number;
  sodium: number;
}

interface Goals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  waterMl: number;
}

function MacroBar({ label, value, goal, color, unit = "g" }: { label: string; value: number; goal: number; color: string; unit?: string }) {
  const pct = goal > 0 ? Math.min(100, (value / goal) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{Math.round(value)}<span className="text-muted-foreground">/{Math.round(goal)}{unit}</span></span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NutritionRing({ eaten, goal }: { eaten: number; goal: number }) {
  const pct = goal > 0 ? Math.min(1, eaten / goal) : 0;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const remaining = Math.max(0, goal - eaten);
  const over = eaten > goal;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
          <circle cx="60" cy="60" r={r} fill="none"
            stroke={over ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
            strokeWidth="10" strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round" className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold">{Math.round(remaining)}</span>
          <span className="text-xs text-muted-foreground">{over ? "over" : "remaining"}</span>
        </div>
      </div>
      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
        <span>🎯 Goal: <strong className="text-foreground">{Math.round(goal)}</strong></span>
        <span>🍽 Eaten: <strong className="text-foreground">{Math.round(eaten)}</strong></span>
      </div>
    </div>
  );
}

const EMPTY_CUSTOM = {
  name: "", calories: "", protein: "", carbs: "", fat: "",
  fiber: "", sugar: "", saturatedFat: "", transFat: "", sodium: "",
  servingSize: "100", servingUnit: "g",
};

export default function MealPlannerPage() {
  const { toast } = useToast();
  const [date, setDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [expandedMeals, setExpandedMeals] = useState<Record<string, boolean>>({ breakfast: true, lunch: true, dinner: true, snacks: true });
  const [addFoodOpen, setAddFoodOpen] = useState(false);
  const [addFoodMeal, setAddFoodMeal] = useState("breakfast");
  const [foodSearch, setFoodSearch] = useState("");
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [goalForm, setGoalForm] = useState<Goals>({ calories: 2000, protein: 150, carbs: 250, fat: 65, fiber: 25, waterMl: 2500 });
  const [customFoodOpen, setCustomFoodOpen] = useState(false);
  const [customFood, setCustomFood] = useState(EMPTY_CUSTOM);
  const [sharingEntry, setSharingEntry] = useState<DiaryEntry | null>(null);

  const { data: diary = [] } = useQuery<DiaryEntry[]>({
    queryKey: ["/api/health/diary", date],
    queryFn: async () => {
      const r = await fetch(`/api/health/diary/${date}`);
      return r.json();
    },
  });

  const { data: water } = useQuery<{ logs: any[]; totalMl: number }>({
    queryKey: ["/api/health/water", date],
    queryFn: async () => {
      const r = await fetch(`/api/health/water/${date}`);
      return r.json();
    },
  });

  const { data: goals } = useQuery<Goals>({
    queryKey: ["/api/health/goals"],
    queryFn: async () => {
      const r = await fetch("/api/health/goals");
      return r.json();
    },
    onSuccess: (g: any) => setGoalForm(g),
  } as any);

  const { data: foods = [], refetch: refetchFoods } = useQuery<FoodItem[]>({
    queryKey: ["/api/health/foods", foodSearch],
    queryFn: async () => {
      const r = await fetch(`/api/health/foods?q=${encodeURIComponent(foodSearch)}&limit=40`);
      return r.json();
    },
    enabled: addFoodOpen,
  });

  const addDiaryMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/health/diary", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/diary", date] });
      setAddFoodOpen(false);
      setSelectedFood(null);
      setQuantity("1");
      toast({ title: "Food added" });
    },
  });

  const deleteDiaryMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/health/diary/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/health/diary", date] }),
    onError: () => {
      toast({ title: "Failed to delete entry", description: "Please try again.", variant: "destructive" });
    },
  });

  const addWaterMutation = useMutation({
    mutationFn: (amountMl: number) => apiRequest("POST", "/api/health/water", { date, amountMl }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/health/water", date] }),
  });

  const updateGoalsMutation = useMutation({
    mutationFn: (data: Goals) => apiRequest("PUT", "/api/health/goals", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/goals"] });
      setGoalsOpen(false);
      toast({ title: "Goals updated" });
    },
  });

  const createCustomFoodMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/health/foods", data),
    onSuccess: () => {
      setCustomFoodOpen(false);
      setCustomFood(EMPTY_CUSTOM);
      refetchFoods();
      toast({ title: "Custom food created" });
    },
  });

  const totals = diary.reduce((acc, e) => ({
    calories: acc.calories + e.calories,
    protein: acc.protein + e.protein,
    carbs: acc.carbs + e.carbs,
    fat: acc.fat + e.fat,
    fiber: acc.fiber + e.fiber,
    sugar: acc.sugar + (e.sugar || 0),
    saturatedFat: acc.saturatedFat + (e.saturatedFat || 0),
    transFat: acc.transFat + (e.transFat || 0),
    sodium: acc.sodium + e.sodium,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, saturatedFat: 0, transFat: 0, sodium: 0 });

  const g = goals || { calories: 2000, protein: 150, carbs: 250, fat: 65, fiber: 25, waterMl: 2500 };
  const waterTotal = water?.totalMl || 0;
  const waterPct = g.waterMl > 0 ? Math.min(100, (waterTotal / g.waterMl) * 100) : 0;

  const handleAddFood = () => {
    if (!selectedFood) return;
    const qty = parseFloat(quantity) || 1;
    const scale = qty;
    const round1 = (v: number) => Math.round(v * scale * 10) / 10;
    addDiaryMutation.mutate({
      date, mealType: addFoodMeal,
      foodName: selectedFood.name,
      foodItemId: selectedFood.id?.startsWith("sys_") ? null : selectedFood.id,
      quantity: qty, servingUnit: selectedFood.servingUnit,
      calories: round1(selectedFood.calories),
      protein: round1(selectedFood.protein),
      carbs: round1(selectedFood.carbs),
      fat: round1(selectedFood.fat),
      fiber: round1(selectedFood.fiber),
      sugar: round1(selectedFood.sugar),
      saturatedFat: round1(selectedFood.saturatedFat || 0),
      transFat: round1(selectedFood.transFat || 0),
      sodium: round1(selectedFood.sodium),
    });
  };

  const mealEntries = (mealType: string) => diary.filter(e => e.mealType === mealType);
  const mealTotal = (mealType: string) => mealEntries(mealType).reduce((s, e) => s + e.calories, 0);

  return (
    <div className="fixed inset-0 flex flex-col bg-background overflow-hidden" style={{ left: "var(--sidebar-width, 256px)" }}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <UtensilsCrossed className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">Meal Planner</h1>
            <div className="flex items-center gap-2 ml-4">
              <Button variant="ghost" size="icon" onClick={() => setDate(format(subDays(parseISO(date), 1), "yyyy-MM-dd"))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" className="font-medium" onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))}>
                {date === format(new Date(), "yyyy-MM-dd") ? "Today" : format(parseISO(date), "MMM d, yyyy")}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setDate(format(addDays(parseISO(date), 1), "yyyy-MM-dd"))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setGoalsOpen(true)}>
            <Settings2 className="w-4 h-4 mr-2" />
            Daily Goals
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Diary */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {MEAL_TYPES.map(meal => (
            <div key={meal.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedMeals(p => ({ ...p, [meal.id]: !p[meal.id] }))}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{meal.icon}</span>
                  <span className="font-semibold">{meal.label}</span>
                  <Badge variant="secondary" className="text-xs">{Math.round(mealTotal(meal.id))} cal</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-7 text-primary" onClick={e => {
                    e.stopPropagation();
                    setAddFoodMeal(meal.id);
                    setAddFoodOpen(true);
                  }}>
                    <Plus className="w-3 h-3 mr-1" />Add
                  </Button>
                  {expandedMeals[meal.id] ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </button>
              {expandedMeals[meal.id] && (
                <div className="border-t border-border">
                  {mealEntries(meal.id).length === 0 ? (
                    <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                      No foods logged yet. Click Add to track your {meal.label.toLowerCase()}.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-12 gap-2 px-4 py-1.5 text-xs text-muted-foreground border-b border-border/50 bg-muted/30">
                        <span className="col-span-5">Food</span>
                        <span className="col-span-2 text-right">Qty</span>
                        <span className="col-span-2 text-right">Cal</span>
                        <span className="col-span-2 text-right">P/C/F</span>
                        <span className="col-span-1" />
                      </div>
                      {mealEntries(meal.id).map(e => (
                        <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center hover:bg-muted/30 border-b border-border/30 last:border-0">
                          <span className="col-span-5 font-medium truncate">{e.foodName}</span>
                          <span className="col-span-2 text-right text-muted-foreground">{e.quantity} {e.servingUnit}</span>
                          <span className="col-span-2 text-right font-medium">{Math.round(e.calories)}</span>
                          <span className="col-span-2 text-right text-xs text-muted-foreground">
                            {Math.round(e.protein)}g/{Math.round(e.carbs)}g/{Math.round(e.fat)}g
                          </span>
                          <div className="col-span-1 flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              onClick={() => setSharingEntry(e)}>
                              <Share2 className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteDiaryMutation.mutate(e.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="px-4 py-2 bg-muted/20 text-xs text-muted-foreground flex gap-4">
                        <span>Subtotal: <strong className="text-foreground">{Math.round(mealTotal(meal.id))} cal</strong></span>
                        <span>P: <strong className="text-foreground">{Math.round(mealEntries(meal.id).reduce((s, e) => s + e.protein, 0))}g</strong></span>
                        <span>C: <strong className="text-foreground">{Math.round(mealEntries(meal.id).reduce((s, e) => s + e.carbs, 0))}g</strong></span>
                        <span>F: <strong className="text-foreground">{Math.round(mealEntries(meal.id).reduce((s, e) => s + e.fat, 0))}g</strong></span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right: Summary Panel */}
        <div className="w-80 flex-shrink-0 border-l border-border bg-card/50 overflow-y-auto p-5 space-y-6">
          {/* Calorie Ring */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Flame className="w-4 h-4 text-orange-500" />Calories</h3>
            <NutritionRing eaten={totals.calories} goal={g.calories} />
          </div>

          {/* Macros */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />Macros</h3>
            <div className="space-y-3">
              <MacroBar label="Protein" value={totals.protein} goal={g.protein} color="bg-blue-500" />
              <MacroBar label="Carbs" value={totals.carbs} goal={g.carbs} color="bg-yellow-500" />
              <MacroBar label="Fat" value={totals.fat} goal={g.fat} color="bg-orange-500" />
              <MacroBar label="Fiber" value={totals.fiber} goal={g.fiber} color="bg-green-500" />
            </div>
          </div>

          {/* Water */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Droplets className="w-4 h-4 text-blue-500" />Water</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{Math.round(waterTotal)}ml</span>
                <span className="font-medium">{Math.round(g.waterMl)}ml goal</span>
              </div>
              <Progress value={waterPct} className="h-3" />
              <div className="flex gap-2 flex-wrap mt-2">
                {WATER_QUICK.map(ml => (
                  <Button key={ml} variant="outline" size="sm" className="text-xs h-7 flex-1"
                    onClick={() => addWaterMutation.mutate(ml)}>
                    +{ml}ml
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Today's Totals */}
          <div>
            <h3 className="font-semibold mb-3">Today's Totals</h3>
            <div className="space-y-1.5 text-sm">
              {[
                { label: "Calories", val: `${Math.round(totals.calories)} kcal` },
                { label: "Protein", val: `${Math.round(totals.protein)}g` },
                { label: "Carbohydrates", val: `${Math.round(totals.carbs)}g` },
                { label: "Fat (Total)", val: `${Math.round(totals.fat)}g` },
                { label: "Saturated Fat", val: `${(Math.round(totals.saturatedFat * 10) / 10)}g`, indent: true },
                { label: "Trans Fat", val: `${(Math.round(totals.transFat * 10) / 10)}g`, indent: true, warn: totals.transFat > 2 },
                { label: "Fiber", val: `${Math.round(totals.fiber)}g` },
                { label: "Sugar", val: `${Math.round(totals.sugar)}g` },
                { label: "Sodium", val: `${Math.round(totals.sodium)}mg` },
              ].map(row => (
                <div key={row.label} className={`flex justify-between ${row.indent ? "pl-3 text-xs" : ""}`}>
                  <span className={`text-muted-foreground ${row.indent ? "before:content-['›'] before:mr-1" : ""}`}>{row.label}</span>
                  <span className={`font-medium ${(row as any).warn ? "text-orange-500" : ""}`}>{row.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add Food Dialog */}
      <Dialog open={addFoodOpen} onOpenChange={setAddFoodOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Food — {MEAL_TYPES.find(m => m.id === addFoodMeal)?.label}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search foods..." value={foodSearch} onChange={e => setFoodSearch(e.target.value)} autoFocus />
            </div>
            <Button variant="outline" onClick={() => setCustomFoodOpen(true)}>Custom</Button>
          </div>
          {selectedFood ? (
            <div className="flex-1 overflow-y-auto">
              <div className="bg-muted/30 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-1">{selectedFood.name}</h3>
                {selectedFood.brand && <p className="text-sm text-muted-foreground mb-3">{selectedFood.brand}</p>}
                <div className="flex items-center gap-3 mb-4">
                  <Label className="text-sm">Quantity</Label>
                  <Input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} className="w-24 h-8" min="0.1" step="0.1" />
                  <span className="text-sm text-muted-foreground">{selectedFood.servingUnit}</span>
                  <span className="text-sm font-medium ml-auto">{Math.round(selectedFood.calories * parseFloat(quantity || "1"))} kcal</span>
                </div>
                {/* Primary macros */}
                <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                  {[
                    { l: "Protein", v: selectedFood.protein, unit: "g" },
                    { l: "Carbs", v: selectedFood.carbs, unit: "g" },
                    { l: "Fat", v: selectedFood.fat, unit: "g" },
                    { l: "Fiber", v: selectedFood.fiber, unit: "g" },
                    { l: "Sugar", v: selectedFood.sugar, unit: "g" },
                    { l: "Sodium", v: selectedFood.sodium, unit: "mg" },
                  ].map(n => (
                    <div key={n.l} className="text-center bg-background rounded p-2">
                      <div className="font-semibold">{Math.round(n.v * parseFloat(quantity || "1") * 10) / 10}{n.unit}</div>
                      <div className="text-xs text-muted-foreground">{n.l}</div>
                    </div>
                  ))}
                </div>
                {/* Fat breakdown */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    { l: "Saturated Fat", v: selectedFood.saturatedFat || 0 },
                    { l: "Trans Fat", v: selectedFood.transFat || 0 },
                  ].map(n => (
                    <div key={n.l} className="text-center bg-background rounded p-2">
                      <div className="font-semibold">{Math.round(n.v * parseFloat(quantity || "1") * 10) / 10}g</div>
                      <div className="text-xs text-muted-foreground">{n.l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedFood(null)}>Back</Button>
                <Button className="flex-1" onClick={handleAddFood} disabled={addDiaryMutation.isPending}>
                  {addDiaryMutation.isPending ? "Adding..." : "Add to Diary"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-1">
              {foods.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {foodSearch ? "No foods found. Try a different search." : "Start typing to search foods..."}
                </div>
              )}
              {foods.map((food, i) => (
                <button key={food.id || i} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors text-left"
                  onClick={() => setSelectedFood(food)}>
                  <div>
                    <div className="font-medium text-sm">{food.name}</div>
                    <div className="text-xs text-muted-foreground">{food.servingSize} {food.servingUnit} · {food.brand || "Generic"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">{Math.round(food.calories)} kcal</div>
                    <div className="text-xs text-muted-foreground">P:{Math.round(food.protein)}g C:{Math.round(food.carbs)}g F:{Math.round(food.fat)}g</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Goals Dialog */}
      <Dialog open={goalsOpen} onOpenChange={setGoalsOpen}>
        <DialogContent >
          <DialogHeader><DialogTitle>Daily Nutrition Goals</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {[
              { key: "calories", label: "Calories", unit: "kcal" },
              { key: "protein", label: "Protein", unit: "g" },
              { key: "carbs", label: "Carbohydrates", unit: "g" },
              { key: "fat", label: "Fat", unit: "g" },
              { key: "fiber", label: "Fiber", unit: "g" },
              { key: "waterMl", label: "Water", unit: "ml" },
            ].map(f => (
              <div key={f.key} className="flex items-center gap-3">
                <Label className="w-32 text-sm">{f.label}</Label>
                <Input type="number" value={goalForm[f.key as keyof Goals]}
                  onChange={e => setGoalForm(p => ({ ...p, [f.key]: parseFloat(e.target.value) || 0 }))}
                  className="flex-1" />
                <span className="text-sm text-muted-foreground w-8">{f.unit}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalsOpen(false)}>Cancel</Button>
            <Button onClick={() => updateGoalsMutation.mutate(goalForm)} disabled={updateGoalsMutation.isPending}>Save Goals</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Food Dialog */}
      <Dialog open={customFoodOpen} onOpenChange={setCustomFoodOpen}>
        <DialogContent >
          <DialogHeader><DialogTitle>Add Custom Food</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Food Name *</Label>
              <Input value={customFood.name} onChange={e => setCustomFood(p => ({ ...p, name: e.target.value }))} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Serving Size</Label>
                <Input type="number" value={customFood.servingSize} onChange={e => setCustomFood(p => ({ ...p, servingSize: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Unit</Label>
                <Input value={customFood.servingUnit} onChange={e => setCustomFood(p => ({ ...p, servingUnit: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "calories", label: "Calories (kcal)" },
                { key: "protein", label: "Protein (g)" },
                { key: "carbs", label: "Carbs (g)" },
                { key: "fat", label: "Fat (g)" },
                { key: "fiber", label: "Fiber (g)" },
                { key: "sugar", label: "Sugar (g)" },
                { key: "saturatedFat", label: "Saturated Fat (g)" },
                { key: "transFat", label: "Trans Fat (g)" },
                { key: "sodium", label: "Sodium (mg)" },
              ].map(f => (
                <div key={f.key}>
                  <Label className="text-sm">{f.label}</Label>
                  <Input type="number" value={customFood[f.key as keyof typeof customFood]}
                    onChange={e => setCustomFood(p => ({ ...p, [f.key]: e.target.value }))} className="mt-1" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomFoodOpen(false)}>Cancel</Button>
            <Button onClick={() => createCustomFoodMutation.mutate(customFood)} disabled={!customFood.name || createCustomFoodMutation.isPending}>
              Save Food
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sharingEntry && (
        <ShareDialog
          isOpen={!!sharingEntry}
          onClose={() => setSharingEntry(null)}
          itemId={sharingEntry.id}
          itemType="meal_plan"
          itemTitle={`${sharingEntry.foodName} (${sharingEntry.mealType})`}
        />
      )}
    </div>
  );
}
